import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bell, FileText, Briefcase } from 'lucide-react'
import { garageApi, formatInTz, useLocalStorage } from '@besiktning/ui'
import type { Booking, InspectionRequest } from '@besiktning/ui'
import { t } from '../i18n'

// Polls the lists the Requests and Jobs screens already use and flags what was
// created since this garage last opened the bell: new incoming requests and
// jobs the customer just booked. No new endpoint, no server-side read state:
// the "last seen" stamp lives in localStorage.
// ponytail: 60s polling, swap for a websocket if the delay ever bothers anyone.

type Item = {
  key: string
  path: string
  createdAt: string
  isBooking: boolean
  title: string
  subtitle: string
}

// Compact enough for a 320px panel: "5 sep 14:00".
const shortDate = (date: string) =>
  formatInTz(date, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

function requestItem(request: InspectionRequest): Item {
  return {
    key: `r${request.id}`,
    path: `/requests/${request.id}`,
    createdAt: request.created_at,
    isBooking: false,
    title: [request.vehicle?.make, request.vehicle?.model, request.vehicle?.reg_number]
      .filter(Boolean)
      .join(' '),
    subtitle: [request.kommun, shortDate(request.created_at)].filter(Boolean).join(' • '),
  }
}

function bookingItem(booking: Booking): Item {
  // Service bookings carry no vehicle at all; only inspection jobs have one
  // through the quote.
  const regNumber = booking.quote?.inspection_request?.vehicle?.reg_number
  return {
    key: `b${booking.id}`,
    path: `/jobs/${booking.id}`,
    createdAt: booking.created_at,
    isBooking: true,
    title:
      [booking.user?.name, regNumber].filter(Boolean).join(' • ') || t('layout.notifBooking'),
    // The booked time, not created_at: that is what the garage plans around.
    subtitle: [booking.title, booking.scheduled_at && shortDate(booking.scheduled_at)]
      .filter(Boolean)
      .join(' • '),
  }
}

export default function NotificationBell({ garageId }: { garageId: number }) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  // Epoch ms, not an ISO string: the API stamps created_at with the brand's
  // UTC offset, so string comparison against a "Z" stamp would misfire.
  const [seenAt, setSeenAt] = useLocalStorage<number>(
    `garage:${garageId}:requestsSeenAt`,
    Date.now(),
  )
  // Snapshot taken when the panel opens, so the list stays visible after the
  // stamp moves forward.
  const [shown, setShown] = useState<Item[]>([])

  const { data: requests = [] } = useQuery({
    queryKey: ['requests', 'open'],
    queryFn: () => garageApi.getRequests({ status: 'open' }),
    refetchInterval: 60_000,
  })

  // Same key as the Jobs screen, so the two share one cache entry.
  const { data: bookings = [] } = useQuery({
    queryKey: ['jobs', 'garage'],
    queryFn: () => garageApi.getBookings(),
    refetchInterval: 60_000,
  })

  const items = [
    ...(requests as InspectionRequest[]).filter((r) => !r.cancelled).map(requestItem),
    // Only jobs still ahead of the garage: a job already done or called off is
    // not news, however recently the row was created.
    ...(bookings as Booking[])
      .filter((b) => !['completed', 'cancelled'].includes(b.status))
      .map(bookingItem),
  ].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))

  const unread = items.filter((i) => Date.parse(i.createdAt) > seenAt)

  const open = () => {
    setShown(unread.length > 0 ? unread : items.slice(0, 5))
    setSeenAt(Date.now())
    setIsOpen(true)
  }

  const go = (path: string) => {
    setIsOpen(false)
    navigate(path)
  }

  return (
    <div className="relative">
      <button
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className="relative p-2 rounded-lg hover:bg-gray-100"
        aria-label={t('layout.notifTitle')}
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="px-4 py-3 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-900">{t('layout.notifTitle')}</p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {shown.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500 text-center">
                  {t('layout.notifEmpty')}
                </p>
              ) : (
                shown.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => go(item.path)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 flex items-start gap-3"
                  >
                    {item.isBooking ? (
                      <Briefcase className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {item.title}
                      </span>
                      <span className="block text-xs text-gray-500">{item.subtitle}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
            <div className="flex border-t border-gray-200">
              <button
                onClick={() => go('/requests')}
                className="flex-1 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-50"
              >
                {t('layout.notifViewRequests')}
              </button>
              <button
                onClick={() => go('/jobs')}
                className="flex-1 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-50 border-l border-gray-200"
              >
                {t('layout.notifViewJobs')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
